import { Button, Icon } from '@arcadiapower/shrike';
import { useEffect, useState } from 'react';
import { TwoColumnGrid } from '~/components/layout';
import { Description, StyledUl, Title } from '~/components/typography';
import { useCopy } from '~/hooks';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { ContractVersion } from '~/typings/graphql';
import { ContractUpload } from './contract-upload.component';

export const ContractsUpload = ({
  context,
  next,
  overrideCopy,
  loading,
  externalSystem,
}: ContractsUploadProps) => {
  const copy = useCopy(context, checkCopy, overrideCopy);
  const { funnelPacket, docuSignContracts, vnmSolarProjectContracts } = context;
  const userId = funnelPacket?.user?.id;
  const utilityAccountId = funnelPacket?.utilityAccount?.id;
  const allContracts = vnmSolarProjectContracts?.concat(docuSignContracts) as ContractVersion[];
  const selfAttestationContract = allContracts?.find(contract => contract?.contractType === 'lmi');

  const [uploadsCompleted, setUploadsCompleted] = useState({});
  const [allUploadsCompleted, setAllUploadsCompleted] = useState({});
  const [allowContinue, setAllowContinue] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    const uploadObject = {};
    allContracts.forEach(contract => {
      uploadObject[contract.id] = false;
    });
    setUploadsCompleted(uploadObject);
    setAllUploadsCompleted(uploadObject);
  }, [allContracts.length]);

  const onUpload = id => {
    setUploadsCompleted(prevUploads => ({
      ...prevUploads,
      [id]: !prevUploads[id],
    }));
  };

  const onUploadSuccess = id => {
    setAllUploadsCompleted(prevUploads => ({
      ...prevUploads,
      [id]: !prevUploads[id],
    }));
  };

  useEffect(() => {
    const allowContinue = Object.entries(allUploadsCompleted)
      .filter(([id]) => Number(id) !== Number(selfAttestationContract?.id))
      .every(([_, uploadStatus]) => uploadStatus);

    setAllowContinue(allowContinue);
  }, [allUploadsCompleted]);

  useEffect(() => {
    const disabled = Object.values(uploadsCompleted).some(uploadStatus => uploadStatus);

    setDisabled(disabled);
  }, [uploadsCompleted]);

  return (
    <TwoColumnGrid>
      <TwoColumnGrid.Primary>
        <Title fontWeight={700} fontSize="24px">
          {copy.title}
        </Title>
        <Description mb={4}>{copy.descriptionOne}</Description>
        <Description mb={4}>{copy.descriptionTwo}</Description>
        {allContracts &&
          allContracts.map(contract => (
            <ContractUpload
              uniqueKey={contract?.id}
              userId={userId}
              externalSystem={externalSystem}
              utilityAccountId={utilityAccountId}
              contract={contract}
              onUpload={onUpload}
              onUploadSuccess={onUploadSuccess}
              disabled={disabled}
            />
          ))}
        <Button
          data-testid="next-button"
          type="submit"
          mode="primary"
          margin={{ top: '40px' }}
          loading={loading}
          disabled={!allowContinue}
          onClick={() => {
            next();
          }}
        >
          {copy.next}
        </Button>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        {!!allContracts.length && (
          <Title fontWeight={700} fontSize="24px" marginTop="190px">
            Contracts upload status
          </Title>
        )}
        <StyledUl>
          {allContracts &&
            allContracts.map(contract => (
              <li key={contract?.id}>
                <Icon
                  margin="4px 8px 0 8px"
                  icon={
                    allUploadsCompleted[contract?.id]
                      ? 'CheckmarkBoxInverted'
                      : 'CheckmarkBoxPartialInverted'
                  }
                  color={allUploadsCompleted[contract?.id] ? 'successBright' : 'warningBright'}
                  scale={2}
                />
                {contract?.displayName}
              </li>
            ))}
        </StyledUl>
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const checkCopy = () => {
  return defaultCopy();
};

export const defaultCopy = () => {
  return {
    title: 'Upload your contracts',
    descriptionOne:
      'Upload your contracts under the respective titles. Click continue when you are done.',
    descriptionTwo: 'Once a contract is uploaded (submitted), you will not be able to edit it.',
    next: 'Continue',
  };
};

export type ContractsUploadCopy = ReturnType<typeof defaultCopy>;

export interface ContractsUploadProps {
  context: ContractsUploadContext;
  loading: boolean;
  next: () => void;
  externalSystem?: string | undefined;
  overrideCopy?: (context: ContractsUploadContext) => Partial<ReturnType<typeof defaultCopy>>;
}

export interface ContractsUploadContext {
  funnelPacket: FetchFunnelPacketResponse;
  docuSignContracts: Partial<ContractVersion>[];
  vnmSolarProjectContracts: Partial<ContractVersion>[];
}
